import { useCallback, useEffect, useState, type FormEvent } from "react";

import { api, type Routine, type RoutineInput, type RoutineType } from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ErrorAlert } from "@/components/error-alert";
import { PageSkeleton } from "@/components/page-skeleton";
import { formatMinutes } from "@/lib/color";
import { cn } from "@/lib/utils";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

type DraftAction = { key: number; title: string; minutes: string };
type Draft = { title: string; type: RoutineType; description: string; actions: DraftAction[] };

let keySeq = 1;
const newAction = (title = "", minutes = "10"): DraftAction => ({
  key: keySeq++,
  title,
  minutes,
});

function draftOf(r: Routine | null): Draft {
  if (!r)
    return {
      title: "",
      type: "morning",
      description: "",
      actions: [newAction(), newAction(), newAction()],
    };
  return {
    title: r.title,
    type: r.type,
    description: r.description,
    actions: r.actions.map((a) =>
      newAction(a.title, String(a.duration_minutes)),
    ),
  };
}

export function RoutinesPage() {
  const [routines, setRoutines] = useState<Routine[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<"new" | Routine | null>(null);

  const load = useCallback(async () => {
    try {
      setRoutines((await api.routines()).routines);
      setError(null);
    } catch (err) {
      setError(messageOf(err, "could not load Routines"));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && !routines) return <ErrorAlert message={error} />;
  if (!routines) return <PageSkeleton />;

  return (
    <div className="grid gap-4">
      {error && <ErrorAlert message={error} />}
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Routines</CardTitle>
            <CardDescription>
              A Routine is a few small actions done back to back in one calendar
              slot, like a morning routine. Order them by priority: when time is
              short, the top ones are kept. Start one from Today.
            </CardDescription>
          </div>
          <Button onClick={() => setEditing(editing === "new" ? null : "new")}>
            {editing === "new" ? "Close" : "New Routine"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {editing !== null && (
            <RoutineEditor
              key={editing === "new" ? "new" : editing.id}
              routine={editing === "new" ? null : editing}
              onClose={() => setEditing(null)}
              onSaved={async () => {
                setEditing(null);
                await load();
              }}
            />
          )}
          {routines.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No Routines yet. Create your first one.
            </p>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {routines.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setEditing(r)}
                    className={cn(
                      "w-full rounded-lg border bg-card p-3 text-left shadow-sm transition-colors hover:bg-secondary/60",
                      editing !== null &&
                        editing !== "new" &&
                        editing.id === r.id
                        ? "border-primary"
                        : "border-border",
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">
                        {r.title}
                      </span>
                      <Badge variant="secondary" className="capitalize">
                        {r.type}
                      </Badge>
                      <Badge variant="outline">
                        {formatMinutes(r.total_minutes)}
                      </Badge>
                    </div>
                    {r.description && (
                      <p className="mt-1 truncate text-xs text-muted-foreground">
                        {r.description}
                      </p>
                    )}
                    <ol className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                      {r.actions.map((a) => (
                        <li key={a.id ?? a.title} className="flex gap-2">
                          <span className="min-w-0 flex-1 truncate">
                            {a.title}
                          </span>
                          <span className="tabular-nums">
                            {a.duration_minutes} min
                          </span>
                        </li>
                      ))}
                    </ol>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RoutineEditor({
  routine,
  onClose,
  onSaved,
}: {
  routine: Routine | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(routine));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const total = draft.actions.reduce((s, a) => s + (Number(a.minutes) || 0), 0);

  function setAction(key: number, patch: Partial<DraftAction>) {
    setDraft((d) => ({
      ...d,
      actions: d.actions.map((a) => (a.key === key ? { ...a, ...patch } : a)),
    }));
  }
  function move(index: number, dir: -1 | 1) {
    setDraft((d) => {
      const next = [...d.actions];
      const j = index + dir;
      if (j < 0 || j >= next.length) return d;
      [next[index], next[j]] = [next[j], next[index]];
      return { ...d, actions: next };
    });
  }
  function remove(key: number) {
    setDraft((d) => ({
      ...d,
      actions: d.actions.filter((a) => a.key !== key),
    }));
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const body: RoutineInput = {
      title: draft.title.trim(),
      type: draft.type,
      description: draft.description.trim(),
      // Row order is the priority: the top action is kept first when time is short.
      actions: draft.actions
        .filter((a) => a.title.trim() !== "")
        .map((a, i) => ({
          title: a.title.trim(),
          duration_minutes: Number(a.minutes) || 0,
          priority: i + 1,
        })),
    };
    setBusy(true);
    setError(null);
    try {
      if (routine) await api.updateRoutine(routine.id, body);
      else await api.createRoutine(body);
      await onSaved();
    } catch (err) {
      setError(messageOf(err, "could not save Routine"));
    }
    setBusy(false);
  }

  async function handleDelete() {
    if (!routine) return;
    if (!window.confirm(`Delete the Routine "${routine.title}"?`)) return;
    setBusy(true);
    try {
      await api.deleteRoutine(routine.id);
      await onSaved();
    } catch (err) {
      setError(messageOf(err, "could not delete Routine"));
    }
    setBusy(false);
  }

  return (
    <form
      className="rounded-xl border border-border bg-secondary/40 p-4"
      onSubmit={(ev) => void handleSubmit(ev)}
    >
      <p className="text-sm font-medium">
        {routine ? `Edit "${routine.title}"` : "New Routine"}
      </p>
      <div className="mt-3 grid gap-4 md:grid-cols-3">
        <div className="grid gap-1.5">
          <Label htmlFor="routine-title">Name</Label>
          <Input
            id="routine-title"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder="Morning routine"
            required
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="routine-type">Type</Label>
          <Select
            value={draft.type}
            onValueChange={(v) => setDraft({ ...draft, type: v as RoutineType })}
          >
            <SelectTrigger id="routine-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="morning">Morning</SelectItem>
              <SelectItem value="evening">Evening</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="routine-description">Description</Label>
          <Input
            id="routine-description"
            value={draft.description}
            onChange={(e) =>
              setDraft({ ...draft, description: e.target.value })
            }
            placeholder="Optional"
          />
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-medium">
            Actions, in priority order
          </span>
          <span className="text-xs text-muted-foreground">
            Total {formatMinutes(total)}
          </span>
        </div>
        <ol className="mt-2 space-y-2">
          {draft.actions.map((a, i) => (
            <li key={a.key} className="flex flex-wrap items-center gap-2">
              <span className="w-5 text-right text-xs tabular-nums text-muted-foreground">
                {i + 1}.
              </span>
              <Input
                aria-label={`Action ${i + 1} title`}
                className="min-w-40 flex-1"
                value={a.title}
                onChange={(e) => setAction(a.key, { title: e.target.value })}
                placeholder="Brush teeth"
              />
              <div className="flex items-center gap-1.5">
                <Input
                  aria-label={`Action ${i + 1} minutes`}
                  className="w-20"
                  type="number"
                  min={1}
                  value={a.minutes}
                  onChange={(e) =>
                    setAction(a.key, { minutes: e.target.value })
                  }
                />
                <span className="text-xs text-muted-foreground">min</span>
              </div>
              <div className="flex gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  aria-label="Move up"
                >
                  ↑
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => move(i, 1)}
                  disabled={i === draft.actions.length - 1}
                  aria-label="Move down"
                >
                  ↓
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(a.key)}
                  aria-label="Remove action"
                >
                  ✕
                </Button>
              </div>
            </li>
          ))}
        </ol>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="mt-2"
          onClick={() =>
            setDraft({ ...draft, actions: [...draft.actions, newAction()] })
          }
        >
          Add action
        </Button>
      </div>

      {error && (
        <div className="mt-3">
          <ErrorAlert message={error} />
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button type="submit" disabled={busy}>
          {routine ? "Save Routine" : "Create Routine"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={busy}
        >
          Cancel
        </Button>
        {routine && (
          <Button
            type="button"
            variant="ghost"
            className="ml-auto text-destructive"
            onClick={() => void handleDelete()}
            disabled={busy}
          >
            Delete
          </Button>
        )}
      </div>
    </form>
  );
}
